import { createApp } from "vue";
import App from "./App.vue";
import axios from "axios";
import { OktaAuth } from "@okta/okta-auth-js";
import OktaVue from "@okta/okta-vue";
import router from "./router";
import store from "./store";
import sfConfig from '@/config';
import { releaseBootstrapGate } from './bootstrapGate';
import { devSkipBootstrap } from './devMode';

const TOKEN_WAIT_MS = 25000;
const TOKEN_POLL_MS = 150;
const RELOAD_KEY = 'storyfinder_bootstrap_token_reload';

const oktaAuth = new OktaAuth(sfConfig.oidc);

async function waitForAccessToken (oktaAuth, maxWaitMs = TOKEN_WAIT_MS) {
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    const tokenObj = await oktaAuth.tokenManager.get('accessToken');
    if (tokenObj?.accessToken) {
      return tokenObj.accessToken;
    }
    await new Promise((resolve) => setTimeout(resolve, TOKEN_POLL_MS));
  }
  const last = await oktaAuth.tokenManager.get('accessToken');
  return last?.accessToken ?? null;
}

const app = createApp(App);
app.use(store).use(router).use(OktaVue, { oktaAuth });

app.config.globalProperties.lambdaUrl = sfConfig.lambdaUrl;
app.config.globalProperties.$http = axios;
app.config.globalProperties.automationConfigUrl = sfConfig.automationConfigUrl;
app.config.globalProperties.pubWiseIssueLimit = sfConfig.publicationWiseIssueLimit ?? [];
app.config.globalProperties.imageTokenUrl = sfConfig.imageTokenUrl;
app.config.globalProperties.imageTokenApiBearerToken = "";

app.mount('#app');

async function runAppBootstrap () {
  let leavingPage = false;
  try {
    store.commit('setAppBootstrap', { loading: true, errored: false, ready: false });

    if (oktaAuth.isLoginRedirect()) {
      await oktaAuth.handleLoginRedirect();
    }

    await oktaAuth.authStateManager.updateAuthState();

    // Only poll for an access token when the user already has an Okta session.
    // Unauthenticated users should be sent to login immediately.
    if (!(await oktaAuth.isAuthenticated())) {
      sessionStorage.removeItem(RELOAD_KEY);
      console.warn('User not authenticated; starting Okta sign-in redirect.');
      leavingPage = true;
      await oktaAuth.signInWithRedirect();
      return;
    }

    let accessToken = await waitForAccessToken(oktaAuth);

    if (!accessToken) {
      // Authenticated but token not ready yet — one reload, then a short wait, then re-login.
      if (!sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        leavingPage = true;
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(RELOAD_KEY);

      accessToken = await waitForAccessToken(oktaAuth, 8000);
      if (!accessToken) {
        console.warn('Access token not available after wait; starting Okta sign-in redirect.');
        leavingPage = true;
        await oktaAuth.signInWithRedirect();
        return;
      }
    }

    sessionStorage.removeItem(RELOAD_KEY);

    const fetchSecApi = sfConfig.fetchSecApi;

    // Automation config bearer token fetch
    const resFetchApi2 = await axios.get(
      `${fetchSecApi}?secretName=${encodeURIComponent(sfConfig.secAutoConfigName)}`,
      {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
      }
    );
    if (resFetchApi2.status !== 200) {
      throw new Error(`HTTP error! Status: ${resFetchApi2.status} while fetching secret`);
    }
    const automationConfigToken = resFetchApi2.data.message;

    // Image token bearer token fetch
    const resFetchApi3 = await axios.get(
      `${fetchSecApi}?secretName=${encodeURIComponent(sfConfig.secImgTokenName)}`,
      {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
      }
    );
    if (resFetchApi3.status !== 200) {
      throw new Error(`HTTP error! Status: ${resFetchApi3.status} while fetching secret`);
    }
    app.config.globalProperties.imageTokenApiBearerToken = resFetchApi3.data.message;

    const postData = {
      view: 'view_storyfinder_issues_display'
    };

    const response = await axios.post(
      `${app.config.globalProperties.automationConfigUrl}`,
      postData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${automationConfigToken}`
        }
      }
    );
    console.log(response);

    app.config.globalProperties.pubWiseIssueLimit = response.data;
    console.log('view_storyfinder_issues_display loaded from Automation Config API');
    console.log(app.config.globalProperties.pubWiseIssueLimit);
  } catch (error) {
    if (leavingPage) {
      console.error('Okta redirect or reload failed:', error);
      releaseBootstrapGate();
      store.commit('setAppBootstrap', { loading: false, errored: true, ready: true });
      return;
    }
    console.error('Failed to fetch secret or view_storyfinder_issues_display from Automation Config API, using fallback:', error);
    app.config.globalProperties.pubWiseIssueLimit = sfConfig.publicationWiseIssueLimit ?? [];
  } finally {
    if (!leavingPage) {
      releaseBootstrapGate();
      store.commit('setAppBootstrap', { loading: false, errored: false, ready: true });
    }
  }
}

if (devSkipBootstrap) {
  console.warn('[devMode] VUE_APP_DEV_SKIP_BOOTSTRAP: skipping runAppBootstrap (using config fallback for pubWiseIssueLimit).');
  releaseBootstrapGate();

  app.config.globalProperties.imageTokenApiBearerToken = "MD4CAQACCQCk3BxDb8Rv8QIDAQABAghE7Yae+1d3cQIFANqsJwUCBQDBAGD9AgQX65cFAgUAkmptKQIEfh64AQ==";
  store.commit('setAppBootstrap', { loading: false, errored: false, ready: true });
} else {
  runAppBootstrap();
}
